import { useTranslation } from 'react-i18next';
import { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useSignModal, ChildSelector, GdprConsent, SigningMethodSelector } from './SignModal/index';
import { InteractiveFieldsForm } from './parent/DynamicFields/InteractiveFieldsForm';
import { DeclarationTextWithFields } from './parent/DynamicFields/DeclarationTextWithFields';
import { hasPlaceholders, getReferencedFieldIds } from '../utils/placeholders';
import { renderVersionPlaceholder } from '@shared/utils/declarationVersionText';
import { countPendingDecisions } from './parent/DynamicFields/fieldDecisionState';
import type { Assignment, Child } from '@shared/types';
import Tooltip from './Tooltip';
import { ChevronDown, ChevronRight, Scale, AlertTriangle } from 'lucide-react';
import { Badge } from './ui';

interface SignModalProps {
  assignment: Assignment;
  children: Child[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function SignModal({ assignment, children, onClose, onSuccess }: SignModalProps) {
  const { t, i18n } = useTranslation();

  const {
    mode,
    setMode,
    allowAllChildren,
    selectedChildId,
    setSelectedChildId,
    signatureData,
    setSignatureData,
    signingMethod,
    setSigningMethod,
    verificationCode,
    enteredCode,
    setEnteredCode,
    gdprConsent,
    setGdprConsent,
    loading,
    error,
    declarationText,
    declarationTitleSnapshot,
    shownDeclarationVersion,
    shownVersionPublishedAtMillis,
    loadingDeclaration,
    pendingCheck,
    selectedChildNotPending,
    selectionNotPending,
    nothingPending,
    // Interactive Fields
    fieldsSchema,
    fieldValues,
    setFieldValues,
    fieldErrors,
    hasInteractiveFields,
    // Scroll tracking
    hasScrolledToBottom,
    setHasScrolledToBottom,
    // Legal binding (bilingual)
    germanDeclarationText,
    legalDisclaimerAccepted,
    setLegalDisclaimerAccepted,
    currentLanguage,
    eligibleChildren,
    requiresParentalConsent,
    hasUnknownAge,
    handleSign,
  } = useSignModal({ assignment, children, onSuccess });

  // German text toggle state
  const [germanExpanded, setGermanExpanded] = useState(false);

  /**
   * Потвърждение преди ОТКАЗ на декларацията.
   *
   * 🔴 Защо съществува: два съседни бутона споделяха корен на всички езици —
   * „Отказ"/„Откажи", `Abbrechen`/`Ablehnen`, `Cancel`/`Decline` — а червеното
   * дърпаше окото към НЕОБРАТИМИЯ. По Р-8.4 на `declined` не се праща
   * напомняне, тоест сгрешеният клик тихо вади семейството от кампанията и
   * никой не го потърсва. Текстовете вече се различават; стъпката е второто
   * ниво защита.
   *
   * ⚠️ Само на отказа, не на подписването.
   */
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  /**
   * 🔴 Отмяната е ПОДРАЗБИРАЩИЯТ СЕ изход: `Esc` затваря диалога, БЕЗ да
   * отказва декларацията. Диалог, който при `Esc` извършва необратимото,
   * произвежда точно грешката, която трябва да спре.
   */
  useEffect(() => {
    if (!showDeclineConfirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowDeclineConfirm(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDeclineConfirm]);

  // Ref for the declaration text scroll container
  const declarationScrollRef = useRef<HTMLDivElement>(null);

  /**
   * 🔴 Задача 43: смяната на избора мени текста (имената) и нулира „прочетено до края". Ако
   * родителят вече е в края и височината не се мени, скрол събитие не идва и гардът би заседнал —
   * затова текстът се връща в началото: нулирането значи „прочети отново". Първото зареждане не се
   * брои (няма какво да се връща).
   */
  const shownTextRef = useRef<string | null>(null);
  useEffect(() => {
    if (loadingDeclaration) return;
    if (shownTextRef.current !== null && shownTextRef.current !== declarationText && declarationScrollRef.current) {
      declarationScrollRef.current.scrollTop = 0;
    }
    shownTextRef.current = declarationText;
  }, [declarationText, loadingDeclaration]);

  /**
   * Колко решения ОСТАВАТ — един източник с маркировката на самите полета.
   *
   * ⚠️ Смята се от `fieldDecisionState`, същата функция, която оцветява полето.
   * Ако броячът имаше собствена сметка, щеше да обещава решения, които екранът
   * не показва къде са.
   */
  const pendingDecisions = useMemo(
    () => countPendingDecisions(fieldsSchema ?? [], fieldValues, fieldErrors),
    [fieldsSchema, fieldValues, fieldErrors],
  );

  /**
   * Скок до първото поле, което още чака решение.
   *
   * Търси по `data-decision-state="pending"` — атрибутът, който самият рендер
   * слага. Тоест броячът и скокът виждат ТОЧНО каквото вижда родителят; ако
   * маркировката се смени, скокът се чупи видимо, не тихо.
   */
  const jumpToNextPending = useCallback(() => {
    const target = declarationScrollRef.current?.querySelector('[data-decision-state="pending"]');
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Check if content needs scrolling (is taller than container)
  const checkIfScrollable = useCallback(() => {
    const container = declarationScrollRef.current;
    if (!container) return false;
    return container.scrollHeight > container.clientHeight + 10; // 10px tolerance
  }, []);

  // Handle scroll to detect when user reaches bottom
  const handleScroll = useCallback(() => {
    const container = declarationScrollRef.current;
    if (!container || hasScrolledToBottom) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Consider "bottom" when within 20px of the actual bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  }, [hasScrolledToBottom, setHasScrolledToBottom]);

  // Auto-mark as scrolled if content doesn't need scrolling
  useEffect(() => {
    if (!loadingDeclaration && declarationText) {
      // Small delay to ensure content is rendered
      const timer = setTimeout(() => {
        if (!checkIfScrollable()) {
          setHasScrolledToBottom(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loadingDeclaration, declarationText, checkIfScrollable, setHasScrolledToBottom]);

  /**
   * Текстът, КАКТО СЕ ПОКАЗВА — с разгърнат `{{version}}`.
   *
   * 🔴 Разгръщането е ТУК, при рендера, а НЕ в `loadDeclaration`. Причината е, че
   * `declarationText` е и снимката, която подписът пази
   * (`useSignModal`: `selectedContentSnapshot: declarationText`). Заместен по-рано,
   * плейсхолдърът изчезва от снимката — и тогава документът няма какво да разгърне.
   * Снимката спира да бъде доказателство, което може да се ВЪЗСТАНОВИ, и става
   * препис, направен от браузъра на родителя, чиято версия е неизвестна
   * (service worker сервира стар код с дни).
   *
   * ⚠️ Функцията е ТАЗИ, която ползва и документът. Дублирана тук, екранът би
   * казал „версия 8", а документът „версия 8 от 16.09.2026 г." — и разминаването
   * се вижда чак когато родителят сравни подписаното с полученото.
   *
   * Текст без плейсхолдър се връща непроменен.
   */
  const contentLanguage = (i18n.language || 'bg').substring(0, 2) as 'bg' | 'de' | 'en';
  const shownDeclarationText = renderVersionPlaceholder(
    declarationText,
    shownDeclarationVersion,
    // `Date` се сглобява ТУК, при рендера. Държан в състоянието, новият обект при
    // всяко изпълнение на ефекта върти безкраен цикъл — виж типа в hook-а.
    shownVersionPublishedAtMillis === undefined ? undefined : new Date(shownVersionPublishedAtMillis),
    contentLanguage,
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              {t('signModal.title')}
            </h2>
            <Tooltip text={t('signModal.cancel')} position="left">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </Tooltip>
          </div>

          {/*
            Броячът на ОСТАВАЩИ решения.
            🔴 Живее в header-а, не в скролващия текст, по две причини: header-ът
            вече е `sticky top-0` с непрозрачен фон, а контейнерът на текста има
            `p-3` — `sticky` вътре в него би се лепнал за PADDING BOX-а и редовете
            щяха да се виждат НАД брояча (капанът, описан в `CLAUDE.md` за таблиците).
            ⚠️ Изчезва сам, когато не остава нищо — брояч, който показва „0 остават",
            заема място, без да отговаря на въпрос.
          */}
          {pendingDecisions.pending > 0 && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
              <span className="text-sm font-medium text-amber-900">
                {t('declarationFields.remaining', {
                  count: pendingDecisions.pending,
                  total: pendingDecisions.total,
                })}
              </span>
              <button
                type="button"
                onClick={jumpToNextPending}
                className="shrink-0 rounded border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                {t('declarationFields.jumpToNext')} ↓
              </button>
            </div>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Declaration Info */}
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium mb-1">
              {t('signModal.declaration')}
            </div>
            <div className="text-lg font-bold text-blue-900">
              {declarationTitleSnapshot || assignment.declarationCode}
            </div>
          </div>

          {/* Child Selector — САМО при известен списък с чакащите (задача 43): празен списък докато
              зарежда би нарисувал „детето го няма", а провалът е „не знам", не „няма". */}
          {/* Нищо за подписване оттук (подписано преди отварянето или междувременно, или в режим „за
              всички“ чакащото е само за дете без запис) → казва се ТОВА, без избор. Числото следва
              режима: „избраните деца“ при „за всички“. */}
          {nothingPending && (
            <div role="status" className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
              {t('signModal.errors.noPendingForSelection', { count: mode === 'all' ? children.length : 1 })}
            </div>
          )}
          {pendingCheck === 'ready' && !nothingPending && (
            <ChildSelector
              children={eligibleChildren}
              mode={mode}
              onModeChange={setMode}
              selectedChildId={selectedChildId}
              onChildSelect={setSelectedChildId}
              allowAllChildren={allowAllChildren}
              selectedChildNotPending={selectedChildNotPending}
            />
          )}
          {pendingCheck === 'failed' && (
            <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {t('signModal.errors.pendingCheckFailed')}
            </div>
          )}

          {/* Declaration Text */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">{t('signModal.declarationTextTitle')}</h3>
              {!loadingDeclaration && !hasScrolledToBottom && (
                <Badge variant="warning">{t('signModal.scrollBadge')}</Badge>
              )}
              {hasScrolledToBottom && (
                <Badge variant="success">{t('signModal.readBadge')}</Badge>
              )}
            </div>
            {loadingDeclaration ? (
              <p className="text-gray-500 italic">{t('signModal.loading')}</p>
            ) : (
              <div
                ref={declarationScrollRef}
                data-testid="declaration-scroll"
                onScroll={handleScroll}
                className={`max-h-60 overflow-y-auto border rounded p-3 bg-gray-50 ${
                  hasScrolledToBottom ? 'border-green-200' : 'border-amber-200'
                }`}
              >
                {/* Check if text has placeholders */}
                {hasInteractiveFields && fieldsSchema && hasPlaceholders(shownDeclarationText) ? (
                  <DeclarationTextWithFields
                    text={shownDeclarationText}
                    fields={fieldsSchema}
                    values={fieldValues}
                    onChange={setFieldValues}
                    errors={fieldErrors}
                    disabled={loading}
                    language="bg"
                  />
                ) : (
                  <p className="text-gray-700 whitespace-pre-wrap text-sm">{shownDeclarationText}</p>
                )}
              </div>
            )}
          </div>

          {/* Interactive Fields (for fields NOT in placeholders) */}
          {hasInteractiveFields && fieldsSchema && (() => {
            // Get field IDs that are referenced in text via placeholders
            const referencedFieldIds = hasPlaceholders(shownDeclarationText)
              ? getReferencedFieldIds(shownDeclarationText)
              : [];

            // Filter out fields that are already shown inline
            const nonInlineFields = fieldsSchema.filter(
              field => !referencedFieldIds.includes(field.id)
            );

            // Only show this section if there are fields not shown inline
            if (nonInlineFields.length === 0) return null;

            return (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-bold mb-3">{t('signModal.additionalInfo')}</h3>
                <InteractiveFieldsForm
                  fields={nonInlineFields}
                  values={fieldValues}
                  onChange={setFieldValues}
                  errors={fieldErrors}
                  disabled={loading}
                  language="bg"
                />
              </div>
            );
          })()}

          {/* German Text Toggle (shown when user's language is not German) */}
          {currentLanguage !== 'de' && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setGermanExpanded(!germanExpanded)}
                className="w-full flex items-center gap-2 px-4 py-3 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
              >
                {germanExpanded ? <ChevronDown size={16} className="text-amber-600" /> : <ChevronRight size={16} className="text-amber-600" />}
                <span className="text-sm font-medium text-amber-800">
                  {t('signModal.legalBinding.toggleGerman')}
                </span>
              </button>
              {germanExpanded && (
                <div className="border-l-4 border-amber-400 bg-amber-50/50 p-4">
                  {germanDeclarationText ? (
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">{germanDeclarationText}</p>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertTriangle size={14} />
                      <span className="text-sm italic">{t('admin.declarations.editor.germanContentMissing')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Legal Binding Disclaimer */}
          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-3">
              <Scale size={16} className="text-blue-600" />
              <h3 className="text-sm font-bold text-blue-900">
                {t('signModal.legalBinding.disclaimerTitle')}
              </h3>
            </div>

            {/* German disclaimer (always shown) */}
            <p className="text-xs text-gray-700 mb-2 italic">
              {t('signModal.legalBinding.disclaimerDE')}
            </p>

            {/* Disclaimer in user's language (if not German) */}
            {currentLanguage !== 'de' && (
              <p className="text-xs text-gray-700 mb-3">
                {t('signModal.legalBinding.disclaimerSelected')}
              </p>
            )}

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={legalDisclaimerAccepted}
                onChange={(e) => setLegalDisclaimerAccepted(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={loading}
              />
              <span className="text-xs text-gray-700">
                {t('signModal.legalBinding.confirmCheckbox')}
              </span>
            </label>
          </div>

          {/* GDPR Consent */}
          <GdprConsent
            consent={gdprConsent}
            onChange={setGdprConsent}
            requiresParentalConsent={requiresParentalConsent}
            hasUnknownAge={hasUnknownAge}
          />

          {/* Signing Method */}
          <SigningMethodSelector
            method={signingMethod}
            onMethodChange={setSigningMethod}
            signatureData={signatureData}
            onSignatureChange={setSignatureData}
            verificationCode={verificationCode}
            enteredCode={enteredCode}
            onEnteredCodeChange={setEnteredCode}
          />

          {/* Error Message — `role=alert`: грешката идва от действие (клик), тоест се ОБЯВЯВА, както
              `pendingCheckFailed` горе; без ролята четецът мълчи, напр. при „изборът се промени". */}
          {error && (
            <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 justify-end pt-4 border-t border-gray-200">
            <button onClick={onClose} className="btn btn-secondary" disabled={loading}>
              {t('signModal.cancel')}
            </button>
            {/*
              🔴 Отказът също чака текста.

              Досега този бутон беше достъпен от първия рендер, а подписването —
              не (`!hasScrolledToBottom` отдолу). Тоест родител можеше да откаже,
              преди Firestore да отговори, и записът не казваше СРЕЩУ КАКВО.
              Измерено на production: 1 запис от 1434, и той е точно отказ.

              ⚠️ И чакаме само ЗАРЕЖДАНЕТО, не прочитането: счупена декларация
              спира да се зарежда с четим резервен текст и тогава отказът е
              възможен. Родител, чиято декларация не работи, има право да откаже.

              🔴 Причината се изписва ВИДИМО (редът отляво), а не в подсказка
              върху бутона: `Tooltip` се отваря на `onMouseEnter`, а браузърите
              не пращат мишкови събития върху disabled контрола. Тоест обяснение
              в подсказка върху заключен бутон може никога да не се появи —
              точно „мъртъв бутон без отговор защо".
            */}
            {loadingDeclaration && (
              <span
                className="mr-auto self-center text-sm text-gray-600"
                data-testid="decline-wait-hint"
              >
                {t('signModal.declineWaitHint')}
              </span>
            )}
            <button
              onClick={() => setShowDeclineConfirm(true)}
              className="btn btn-danger"
              disabled={loading || loadingDeclaration || pendingCheck === 'failed' || nothingPending || selectionNotPending}
            >
              {loading ? t('signModal.signing') : t('signModal.declineButton')}
            </button>
            <Tooltip
              // Заключен заради избора → обяснява известието до избора; подсказка „прочетете до края“ би
              // пращала към текст, който не е пречката.
              text={nothingPending || selectionNotPending ? '' : !hasScrolledToBottom ? t('signModal.errors.scrollHint') : !legalDisclaimerAccepted ? t('signModal.legalBinding.confirmCheckbox') : ''}
              position="top"
            >
              <button
                onClick={() => handleSign('signed')}
                className="btn btn-primary"
                disabled={loading || pendingCheck !== 'ready' || nothingPending || selectionNotPending || !hasScrolledToBottom || !legalDisclaimerAccepted}
              >
                {/*
                  ⚠️ Бутонът казва КАКВО липсва, вместо да мълчи. Той остава
                  активен — валидацията си върши работата при клик и посочва
                  конкретното поле. Disabled бутон без обяснение е по-лош от
                  бутон, който казва какво остава.
                */}
                {loading
                  ? t('signModal.signing')
                  : pendingDecisions.pending > 0
                    ? t('declarationFields.signBlocked', { count: pendingDecisions.pending })
                    : t('signModal.signButton')}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/*
        Потвърждение преди ОТКАЗ.

        ⚠️ Модал над модала → важи правилото за скрол (`modalScrollRatchet`):
        модел (б) — overlay без собствен скрол, панел ограничен по височина със
        свой `overflow-y-auto`. Без него при нисък прозорец бутоните излизат под
        ръба и стават физически недостижими.
        🔴 `z-[60]`, защото основният модал е `z-50` — диалогът трябва да е НАД него.
      */}
      {showDeclineConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
          // 🔴 Клик ИЗВЪН диалога = отмяна, не отказ. Заедно с `Esc` това прави
          // безопасния изход подразбиращ се — и двата пътя водят до „нищо не се случи".
          onClick={() => setShowDeclineConfirm(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="decline-confirm-title"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle size={20} className="text-red-600 shrink-0 mt-0.5" />
              <h3 id="decline-confirm-title" className="text-lg font-bold text-gray-900">
                {t('signModal.declineConfirm.title')}
              </h3>
            </div>
            <p className="text-sm text-gray-700 mb-6">
              {t('signModal.declineConfirm.body')}
            </p>
            <div className="flex flex-wrap gap-3 justify-end">
              {/* Безопасният изход стои ПЪРВИ и е неутрален. */}
              <button
                onClick={() => setShowDeclineConfirm(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('signModal.declineConfirm.cancel')}
              </button>
              <button
                onClick={() => {
                  setShowDeclineConfirm(false);
                  handleSign('declined');
                }}
                className="btn btn-danger"
                disabled={loading}
              >
                {t('signModal.declineConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
